import {
  getChats,
  getAllChats,
  getContacts,
  getAgents,
  getChatFeedback,
  isV2Available
} from '@/services/huggyAPI'

const feedbackCache = new Map()

export default {
  namespaced: true,

  state: {
    chats: [],
    contacts: [],
    agents: [],
    feedbacks: [],

    chatsLoading: false,
    feedbacksLoading: false,
    auxLoading: false,

    dataError: null,

    autoRefresh: true,
    lastUpdated: null,

    filters: {
      dateRange: 'today',
      startDate: '',
      endDate: '',
      channel: 'all',
      status: 'all'
    }
  },

  getters: {
    feedbackStats: (state) => {
      if (state.feedbacks.length === 0) {
        return { avgScore: null, total: 0, distribution: {} }
      }
      const scores = state.feedbacks
        .map((f) => Number(f.feedback.score))
        .filter((s) => Number.isFinite(s) && s > 0)

      const avgScore = scores.length > 0
        ? scores.reduce((sum, s) => sum + s, 0) / scores.length
        : null

      const distribution = {}
      scores.forEach((s) => {
        const label = String(s)
        distribution[label] = (distribution[label] ?? 0) + 1
      })

      return { avgScore, total: scores.length, distribution }
    },

    availableChannels: (state) => {
      const defaults = ['WhatsApp', 'Email', 'Instagram', 'Messenger', 'Chat']
      const fromChats = state.chats
        .map((c) => c?.channels?.[0]?.type ?? c?.channel?.type ?? c?.channel)
        .filter(Boolean)
      return Array.from(new Set([...defaults, ...fromChats]))
    }
  },

  mutations: {
    SET_CHATS (state, chats) {
      state.chats = chats
    },
    SET_CONTACTS (state, contacts) {
      state.contacts = contacts
    },
    SET_AGENTS (state, agents) {
      state.agents = agents
    },
    SET_FEEDBACKS (state, feedbacks) {
      state.feedbacks = feedbacks
    },
    SET_CHATS_LOADING (state, loading) {
      state.chatsLoading = loading
    },
    SET_FEEDBACKS_LOADING (state, loading) {
      state.feedbacksLoading = loading
    },
    SET_AUX_LOADING (state, loading) {
      state.auxLoading = loading
    },
    SET_DATA_ERROR (state, error) {
      state.dataError = error
    },
    SET_AUTO_REFRESH (state, value) {
      state.autoRefresh = value
    },
    SET_LAST_UPDATED (state, timestamp) {
      state.lastUpdated = timestamp
    },
    SET_FILTERS (state, filters) {
      state.filters = { ...state.filters, ...filters }
    }
  },

  actions: {
    async fetchChats ({ state, commit }) {
      commit('SET_CHATS_LOADING', true)
      commit('SET_DATA_ERROR', null)

      try {
        const params = {}
        if (state.filters.status && state.filters.status !== 'all') {
          params.status = state.filters.status
        }
        if (state.filters.channel && state.filters.channel !== 'all') {
          params.channel = state.filters.channel
        }

        const needsPagination = ['last30', 'custom'].includes(state.filters.dateRange)
        const chats = needsPagination ? await getAllChats(params) : await getChats(params)

        commit('SET_CHATS', chats)
        commit('SET_LAST_UPDATED', new Date())
      } catch (error) {
        const status = error?.response?.status
        if (!error?.response) {
          commit('SET_DATA_ERROR', 'A API do Huggy esta indisponivel no momento.')
          commit('SET_AUTO_REFRESH', false)
        } else if (status >= 500) {
          commit('SET_DATA_ERROR', 'Instabilidade detectada na API do Huggy.')
          commit('SET_AUTO_REFRESH', false)
        } else if (status === 401) {
          commit('SET_DATA_ERROR', 'Token da API v2 invalido ou expirado.')
          commit('SET_AUTO_REFRESH', false)
        } else {
          commit('SET_DATA_ERROR', 'Nao foi possivel carregar os dados agora.')
        }
      } finally {
        commit('SET_CHATS_LOADING', false)
      }
    },

    async fetchAuxiliaryData ({ commit }) {
      commit('SET_AUX_LOADING', true)
      try {
        const [contacts, agents] = await Promise.all([getContacts(), getAgents()])
        commit('SET_CONTACTS', Array.isArray(contacts) ? contacts : [])
        commit('SET_AGENTS', Array.isArray(agents) ? agents : [])
      } catch (error) {
        console.error('Error fetching auxiliary data:', error)
      } finally {
        commit('SET_AUX_LOADING', false)
      }
    },

    async fetchFeedbacks ({ state, commit }) {
      if (!isV2Available()) return

      const chatIds = state.chats.map((c) => c?.id).filter(Boolean)
      if (chatIds.length === 0) {
        commit('SET_FEEDBACKS', [])
        return
      }

      const uncachedIds = chatIds.filter((id) => !feedbackCache.has(id))

      if (uncachedIds.length === 0) {
        const cached = chatIds
          .map((id) => ({ chatId: id, feedback: feedbackCache.get(id) }))
          .filter((r) => r.feedback?.score != null && Number(r.feedback.score) > 0)
        commit('SET_FEEDBACKS', cached)
        return
      }

      commit('SET_FEEDBACKS_LOADING', true)

      try {
        const concurrency = 10
        for (let i = 0; i < uncachedIds.length; i += concurrency) {
          const batch = uncachedIds.slice(i, i + concurrency)
          await Promise.all(
            batch.map(async (chatId) => {
              const feedback = await getChatFeedback(chatId)
              feedbackCache.set(chatId, feedback)
            })
          )

          const all = chatIds
            .map((id) => ({ chatId: id, feedback: feedbackCache.get(id) }))
            .filter((r) => r.feedback?.score != null && Number(r.feedback.score) > 0)
          commit('SET_FEEDBACKS', all)
        }
      } catch (error) {
        console.error('Error fetching feedbacks:', error)
      } finally {
        commit('SET_FEEDBACKS_LOADING', false)
      }
    },

    async refresh ({ dispatch }) {
      await Promise.all([
        dispatch('fetchChats'),
        dispatch('fetchFeedbacks')
      ])
    },

    updateFilters ({ commit, dispatch }, filters) {
      commit('SET_FILTERS', filters)
      dispatch('fetchChats')
    }
  }
}
